import React from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { Menu, MessageCircle } from "lucide-react";
import { getChatUnreadCount, getReturnedOrdersCount } from "./services/api";
import { playNotificationSound, showBrowserNotification } from "./utils/notificationSound";
import SideBar from "./components/SideBar";
import Dashboard from "./pages/Dashboard";
import Orders from "./pages/Orders";
import Users from "./pages/Users";
import Products from "./pages/Products";
import DeliveryCompanies from "./pages/DeliveryCompanies";
import ShippingQueue from "./pages/ShippingQueue";
import CreateOrder from "./pages/CreateOrder";
import LandingPage from "./pages/LandingPage";
import CreateStore from "./pages/CreateStore";
import Login from "./pages/Login";
import DeliveryTracking from "./pages/DeliveryTracking";
import UnassignedOrders from "./pages/UnassignedOrders";
import Brands from "./pages/Brands";
import Payroll from "./pages/Payroll";
import Settings from "./pages/Settings";
import StockManagement from "./pages/StockManagement";
import PrivateRoute from "./components/PrivateRoute";
import { AuthProvider } from "./context/AuthContext";
import PresenceHeartbeat from "./components/PresenceHeartbeat";
import TeamChat from "./components/TeamChat";
import { ToastProvider, useToast } from "./context/ToastContext";

function AppContent() {
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isChatOpen, setIsChatOpen] = React.useState(false);
  const isChatOpenRef = React.useRef(false);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [returnedCount, setReturnedCount] = React.useState(0);
  const { addToast } = useToast();
  const publicPages = ["/login", "/landing"];
  const isPublicPage = publicPages.includes(location.pathname);
  const prevUnreadRef = React.useRef(-1);
  
  // Fetch unread count + poll every 5s
  const fetchUnreadCount = React.useCallback(async () => {
    try {
      const res = await getChatUnreadCount();
      if (res.success) {
        const newCount = res.unread;
        // Play sound when unread count increases and chat is closed
        if (newCount > prevUnreadRef.current && prevUnreadRef.current >= 0) {
          if (!isChatOpenRef.current) {
            playNotificationSound();
            
            // Show toast if there are new unread messages
            addToast(`Vous avez ${newCount} message${newCount !== 1 ? 's' : ''} non lu${newCount !== 1 ? 's' : ''}`, 'chat', '💬 Team Chat');
            
            showBrowserNotification('💬 Team Chat', `Vous avez ${newCount} message${newCount !== 1 ? 's' : ''} non lu${newCount !== 1 ? 's' : ''}`);
          }
        }
        prevUnreadRef.current = newCount;
        setUnreadCount(newCount);
      }
    } catch (err) { /* ignore when not logged in */ }
  }, [addToast]);

  const fetchReturnedCount = React.useCallback(async () => {
    try {
      const res = await getReturnedOrdersCount();
      if (res.success) {
        setReturnedCount(res.count);
      }
    } catch (err) { /* ignore */ }
  }, []);

  React.useEffect(() => {
    if (isPublicPage) return;
    fetchUnreadCount();
    fetchReturnedCount();
    const interval = setInterval(() => {
      fetchUnreadCount();
      fetchReturnedCount();
    }, 5000);
    return () => clearInterval(interval);
  }, [isPublicPage, fetchUnreadCount, fetchReturnedCount]);

  // Close sidebar on route change (mobile)
  React.useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex bg-gray-50/50 min-h-screen w-full relative overflow-x-hidden">
      <PresenceHeartbeat />

      {!isPublicPage && (
        <>
          {/* Mobile Overlay */}
          {isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[45] md:hidden transition-opacity"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}

          <SideBar 
            isOpen={isSidebarOpen} 
            setIsOpen={setIsSidebarOpen} 
            setIsChatOpen={(val) => { 
                setIsChatOpen(val); 
                isChatOpenRef.current = val; 
            }} 
            unreadCount={unreadCount} 
            returnedCount={returnedCount}
          />
        </>
      )}

      <main className={`flex-1 min-h-screen w-full flex flex-col ${!isPublicPage ? 'md:ml-64' : ''}`}>
        {!isPublicPage && (
          <header className={`sticky top-0 z-40 ${isSidebarOpen ? 'bg-transparent' : 'bg-white/80 backdrop-blur-md'} border-b border-gray-100 px-4 py-3 flex items-center justify-between md:hidden shadow-sm transition-colors duration-300`}>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-gradient-to-tr from-indigo-600 to-blue-500 rounded-lg flex items-center justify-center shadow-md">
                <Menu className="text-white w-4 h-4" />
              </div>
              <span className="text-lg font-bold text-gray-900">Adminify</span>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => { setIsChatOpen(true); isChatOpenRef.current = true; }}
                className="relative p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <MessageCircle className="w-6 h-6 text-gray-600" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white animate-pulse" />
                )}
              </button>
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <Menu className="w-6 h-6 text-gray-600" />
              </button>
            </div>
          </header>
        )}

        <div className="flex-1">
          <Routes>
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/create-store" element={<CreateStore />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/orders/new"
              element={
                <PrivateRoute roles={["admin", "confirmation"]}>
                  <CreateOrder />
                </PrivateRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <PrivateRoute roles={["admin", "confirmation", "gerant"]}>
                  <Orders />
                </PrivateRoute>
              }
            />

            <Route
              path="/products"
              element={
                <PrivateRoute roles={["admin", "confirmation", "gerant"]}>
                  <Products />
                </PrivateRoute>
              }
            />
            <Route
              path="/delivery-companies"
              element={
                <PrivateRoute roles={["admin", "gerant"]}>
                  <DeliveryCompanies />
                </PrivateRoute>
              }
            />
            <Route
              path="/shipping-queue"
              element={
                <PrivateRoute roles={["admin", "confirmation", "gerant"]}>
                  <ShippingQueue />
                </PrivateRoute>
              }
            />
            <Route
              path="/delivery-tracking"
              element={
                <PrivateRoute roles={["admin", "confirmation", "gerant"]}>
                  <DeliveryTracking />
                </PrivateRoute>
              }
            />
            <Route
              path="/unassigned-queue"
              element={
                <PrivateRoute roles={["admin"]}>
                  <UnassignedOrders />
                </PrivateRoute>
              }
            />
            <Route
              path="/users"
              element={
                <PrivateRoute roles={["admin"]}>
                  <Users />
                </PrivateRoute>
              }
            />
            <Route
              path="/payroll"
              element={
                <PrivateRoute roles={["admin"]}>
                  <Payroll />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute roles={["admin", "confirmation"]}>
                  <Settings />
                </PrivateRoute>
              }
            />
            <Route
              path="/brands"
              element={
                <PrivateRoute roles={["superadmin"]}>
                  <Brands />
                </PrivateRoute>
              }
            />
            <Route
              path="/stock"
              element={
                <PrivateRoute roles={["admin", "gerant"]}>
                  <StockManagement />
                </PrivateRoute>
              }
            />
          </Routes>
        </div>
      </main>

      {/* Floating Chat Button + Panel */}
      {!isPublicPage && (
        <>
          <button
            onClick={() => { setIsChatOpen(true); isChatOpenRef.current = true; }}
            className={`
              fixed bottom-6 right-6 z-[65] w-14 h-14
              bg-gradient-to-r from-indigo-600 to-blue-500
              rounded-full shadow-lg shadow-blue-300/40
              flex items-center justify-center
              hover:shadow-xl hover:shadow-blue-300/60 hover:scale-110
              active:scale-95 transition-all duration-200
              ${isChatOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}
            `}
            title="Team Chat"
          >
            <MessageCircle className="w-6 h-6 text-white" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1 shadow-md animate-pulse">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          <TeamChat isOpen={isChatOpen} onClose={() => { setIsChatOpen(false); isChatOpenRef.current = false; }} onChatOpened={fetchUnreadCount} />
        </>
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
